import { Injectable } from '@angular/core';
import { AGENT_STATUS } from '../model/agent-premium';
import * as _ from 'lodash-es';
import { AgentStatus, AgentLastStatusEnum } from '../model/agent-status';
import { CalculatedPrice } from '../model/calculated-price';
import { ClientStatus } from '../model/client-status';
import { ImageGalleryBuilder } from './image-gallery-builder';
import { Offer } from '../model/offer';
import { Reservation } from '../model/reservation';
import { ReservationExtra } from '../model/reservation-extra';
import { ReservationLogEvent } from '../model/reservation-log-event';
import { ReservationProduct } from '../model/reservation-product';
import { ReservationRequest } from '../model/reservation-request';
import { ReservationTrip } from '../model/reservation-trip';
import { PremiumStatusEnum, StatusPremium } from '../model/status-premium';
import { LocationBuilder } from './location-builder';
import { ProductBuilder } from './product-builder';
import { UserBuilder } from './user-builder';
import { VehicleBuilder } from './vehicle-builder';
import { AgencyBuilder } from './agency-builder';
import { FlightStatsBuilder } from './flight-stats-builder';
import { AdminClientBuilder } from "./adminClient-builder";
import { MechanicalOrderBuilder } from "./mechanical-order-builder";
import { CarServiceFamilyBuilder } from './car-service-family-builder';
import { CustomizedServiceRequestBuilder } from './customizedServiceRequest-builder';
import { newDateBuilder } from '../utils/new-date-builder';
import * as i0 from "@angular/core";
export class ReservationBuilder {
    static buildCalculatedPrice(data) {
        var _a;
        const calculatedPrice = new CalculatedPrice();
        const dataOffer = data.offer;
        const offer = new Offer();
        const trip = {};
        offer.productId = dataOffer.product_id;
        offer.startDate = newDateBuilder(dataOffer.start_date);
        offer.endDate = newDateBuilder(dataOffer.end_date);
        offer.productPrice = dataOffer.product_price;
        offer.productPrice_aena = dataOffer.product_price_aena;
        offer.pvp_product_total = dataOffer.pvp_product_total;
        offer.pvp_additional = dataOffer.pvp_additional;
        offer.pvp_carservice_discount = dataOffer.pvp_carservice_discount;
        offer.pvp_product_dto = dataOffer.pvp_product_dto;
        offer.pvp_product_option = dataOffer.pvp_product_option;
        offer.pvp_product_points = dataOffer.pvp_product_points;
        offer.pvp_product_promotion = dataOffer.pvp_product_promotion;
        offer.pvp_promotion = dataOffer.pvp_promotion;
        offer.pvp_product_raw = dataOffer.pvp_product_raw;
        offer.pvp_product_std = dataOffer.pvp_product_std;
        offer.pvp_product_trans = dataOffer.pvp_product_trans;
        offer.pvp_wash = dataOffer.pvp_wash;
        offer.pvp_double_space = dataOffer.pvp_double_space;
        offer.pvp_mechanical_orders = dataOffer.pvp_mechanical_orders;
        offer.pvp_mechanical_order_ferry = dataOffer.pvp_mechanical_order_ferry;
        offer.pvp_extra_services = dataOffer.pvp_extra_services;
        offer.vat_additional = dataOffer.vat_additional;
        offer.vat_standard = dataOffer.vat_standard;
        offer.vat_trans = dataOffer.vat_trans;
        offer.base_additional = dataOffer.base_additional;
        offer.base_standard = dataOffer.base_standard;
        offer.base_trans = dataOffer.base_trans;
        offer.promos = [];
        offer.mechanical_orders = dataOffer.mechanical_orders ? _.map(dataOffer.mechanical_orders, (mechanicalOrder) => {
            return MechanicalOrderBuilder.buildMechanicalOrder(mechanicalOrder);
        }) : null;
        if (dataOffer.promos) {
            offer.promos =
                dataOffer.promos.map((promo) => ProductBuilder.buildPromo(promo));
        }
        offer.price = dataOffer.total_price;
        offer.hash = dataOffer.hash;
        offer.extras = this.buildExtras(dataOffer.extras);
        offer.availability_status = dataOffer.availability_status;
        calculatedPrice.offer = offer;
        calculatedPrice.extras = this.buildExtras(data.extras);
        calculatedPrice.car_services = (_a = data.car_services) === null || _a === void 0 ? void 0 : _a.map(family => {
            return CarServiceFamilyBuilder.buildCarServiceFamily(family);
        });
        if (data.trip) {
            calculatedPrice.trip = data.trip;
        }
        return calculatedPrice;
    }
    static buildExtras(extrasRaw) {
        let extras = [];
        if (extrasRaw && extrasRaw.length) {
            extrasRaw.forEach((extra) => {
                let extraProduct = ProductBuilder.buildExtra(extra);
                if (extraProduct) {
                    extras.push(extraProduct);
                }
            });
        }
        return extras;
    }
    static buildReservation(data, clientInfo) {
        const reservation = new Reservation();
        reservation.id = data.reservation_id;
        reservation.hash = data.reservation_hash;
        reservation.in_date = data.in_date ? newDateBuilder(data.in_date) : null;
        reservation.out_date = data.out_date ? newDateBuilder(data.out_date) : null;
        reservation.userId = data.client_id;
        reservation.userEmail = data.client_email;
        reservation.profileId = data.profile_id;
        reservation.profileName = data.profile_name;
        reservation.companyId = data.company_id;
        reservation.companyName = data.company_name;
        reservation.costCenterId = data.costcenter_id;
        reservation.costCenterName = data.costcenter_name;
        reservation.product = this.buildReservationProduct(data.product);
        reservation.out_date_requested = data.out_date_requested ? newDateBuilder(data.out_date_requested) : '';
        reservation.reservationDate = data.reservation_date ? newDateBuilder(data.reservation_date) : null;
        reservation.startDate = data.start_date ? newDateBuilder(data.start_date) : null;
        reservation.endDate = data.end_date ? newDateBuilder(data.end_date) : null;
        reservation.trip = this.buildTrip(data.trip);
        reservation.pdfUrl = data.pdf_url;
        reservation.calle = data.calle;
        reservation.spaceNumber = data.space_number;
        reservation.agentArriveCheckIn = data.premium_checkin_agent_arrive_timestamp;
        reservation.agentArriveCheckOut = data.premium_checkout_agent_arrive_timestamp;
        reservation.agentCarPicked = data.premium_checkout_car_picked_timestamp;
        reservation.premium_checkin_car_in_base_timestamp = data.premium_checkin_car_in_base_timestamp;
        reservation.premium_checkin_car_in_base_user_id = data.premium_checkin_car_in_base_user_id;
        reservation.premium_checkout_agent_pick_parking_ticket_timestamp = data.premium_checkout_agent_pick_parking_ticket_timestamp;
        reservation.premium_checkout_agent_renew_parking_ticket_timestamp = data.premium_checkout_agent_renew_parking_ticket_timestamp;
        reservation.lotName = data.lot_name ? data.lot_name : "aparca&go";
        reservation.id_lot = data.id_lot;
        reservation.id_invoice_profile = data.id_invoice_profile;
        reservation.id_client = data.id_client;
        reservation.id_client_origin = data.id_client_origin;
        reservation.id_product = data.id_product;
        reservation.id_product_option = data.id_product_option;
        reservation.is_in_reminder_sent = data.is_in_reminder_sent;
        reservation.is_out_reminder_sent = data.is_out_reminder_sent;
        reservation.num_days = data.num_days;
        reservation.num_minutes = data.num_minutes;
        reservation.points_neo = data.points_neo;
        reservation.was_cancelled = data.was_cancelled;
        reservation.was_rejected = data.was_rejected;
        reservation.was_timeout = data.was_timeout;
        reservation.id_agency = data.id_agency;
        reservation.is_alternative_reservation = data.is_alternative_reservation;
        reservation.sporadic_with_registered_email = data.sporadic_with_registered_email;
        reservation.editable_mechanical_order = data.editable_mechanical_order ? data.editable_mechanical_order : null;
        reservation.calc_mode = data.calc_mode;
        reservation.flightstats_validation = data.flightstats_validation;
        reservation.flightstats = data.flightstats ? FlightStatsBuilder.buildFlightStats(data.flightstats) : null;
        reservation.via = data.via;
        reservation.id_bill = data.id_bill;
        reservation.status = data.status;
        reservation.extras = [];
        reservation.accept_promos = data.accept_promos;
        reservation.accept_newsletter = data.accept_newsletter;
        reservation.accept_terms_timestamp = data.accept_terms_timestamp;
        reservation.keysNeeded = false;
        if (data.extras) {
            reservation.extras =
                data.extras.map((extra) => this.buildReservationExtra(extra));
            reservation.extras.map((extra) => {
                if (extra.extra.keys_needed)
                    reservation.keysNeeded = true;
            });
        }
        reservation.promos = [];
        if (data.promos) {
            reservation.promos =
                data.promos.map((promo) => ProductBuilder.buildPromo(promo));
        }
        reservation.serviceStatus = data.status.service;
        if (data.status.premium) {
            reservation.premiumStatus = this.buildPremiumStatus(data.status.premium);
            reservation.premiumStatus.status = data.status.status;
            reservation.premiumStatus.premiumStatus = data.status.premium_status;
            reservation.premiumStatus.premium_service_status = data.status.premium.premium_service_status;
        }
        reservation.id_key_space = data.id_key_space;
        reservation.key_space_information = data.key_space_information ? this.buildAdminKeySpaceInformation(data.key_space_information) : null;
        reservation.price = data.total_price;
        reservation.paid = data.paid;
        reservation.paid_amount = data.paid_amount;
        reservation.usePoints = data.use_points;
        reservation.paymentMethodId = data.paymethod_id;
        reservation.paymentMethodAlias = data.paymethod_alias;
        reservation.in_progress_id_reservation_payment = data.in_progress_id_reservation_payment;
        reservation.enable_automatic_space = data.enable_automatic_space;
        reservation.key_space_complete_in_operation = isNaN(Date.parse(data.key_space_complete_in_operation)) == false ? data.key_space_complete_in_operation : null;
        reservation.key_space_complete_out_operation = isNaN(Date.parse(data.key_space_complete_out_operation)) == false ? data.key_space_complete_out_operation : null;
        reservation.key_space_start_in_operation = isNaN(Date.parse(data.key_space_start_in_operation)) == false ? data.key_space_start_in_operation : null;
        reservation.key_space_start_out_operation = isNaN(Date.parse(data.key_space_start_out_operation)) == false ? data.key_space_start_out_operation : null;
        reservation.pvp_additional = data.pvp_additional;
        reservation.pvp_product_option = data.pvp_product_option;
        reservation.pvp_product_dto = data.pvp_product_dto;
        reservation.pvp_product_points = data.pvp_product_points;
        reservation.pvp_product_promotion = data.pvp_product_promotion;
        reservation.pvp_product_total = data.pvp_product_total;
        reservation.pvp_product_raw = data.pvp_product_raw;
        reservation.pvp_product_std = data.pvp_product_std;
        reservation.pvp_carservice_discount = data.pvp_carservice_discount;
        reservation.pvp_product_trans = data.pvp_product_trans;
        reservation.pvp_wash = data.pvp_wash;
        reservation.pvp_double_space = data.pvp_double_space;
        reservation.pvp_mechanical_orders = data.pvp_mechanical_orders;
        reservation.pvp_mechanical_order_ferry = data.pvp_mechanical_order_ferry;
        reservation.pvp_extra_services = data.pvp_extra_services;
        reservation.vat_additional = data.vat_additional;
        reservation.vat_standard = data.vat_standard;
        reservation.vat_trans = data.vat_trans;
        reservation.base_additional = data.base_additional;
        reservation.base_standard = data.base_standard;
        reservation.base_trans = data.base_trans;
        if (data.log) {
            reservation.log = data.log.map((event) => this.buildLogEvent(event));
        }
        if (clientInfo) {
            clientInfo.user_id ? reservation.userInfo = UserBuilder.buildUser(clientInfo) : reservation.agencyInfo = AgencyBuilder.buildAgency(clientInfo);
        }
        if (data.agent_observations) {
            reservation.agentObservations = data.agent_observations;
        }
        if (data.client) {
            reservation.client = AdminClientBuilder.buildAdminClient(data.client);
        }
        if (reservation.userId === 1) {
            reservation.invoice_requested = data.invoice_requested;
            reservation.invoice_address = data.invoice_address;
            reservation.invoice_city = data.invoice_city;
            reservation.invoice_email = data.invoice_email;
            reservation.invoice_name = data.invoice_name;
            reservation.invoice_nif = data.invoice_nif;
            reservation.invoice_state = data.invoice_state;
            reservation.invoice_country = data.invoice_country;
            reservation.invoice_zip = data.invoice_zip;
        }
        reservation.reservation_instructions = this.buildReservationInstructions(data.reservation_instructions);
        reservation.is_alternative_reservation = data.is_alternative_reservation ? data.is_alternative_reservation : null;
        reservation.premium_checkout_vehicle_status = data.premium_checkout_vehicle_status;
        reservation.premium_checkout_vehicle_observations = data.premium_checkout_vehicle_observations;
        reservation.premium_checkin_car_in_base_timestamp = data.premium_checkin_car_in_base_timestamp;
        reservation.premium_checkin_car_in_base_user_id = data.premium_checkin_car_in_base_user_id;
        reservation.analytics_category = data.analytics_category;
        reservation.analytics_name = data.analytics_name;
        reservation.id_mechanical_orders = data.id_mechanical_orders;
        reservation.pvp_mechanical_orders = data.pvp_mechanical_orders;
        reservation.pvp_mechanical_order_ferry = data.pvp_mechanical_order_ferry;
        reservation.id_vehicle = data.id_vehicle ? data.id_vehicle : null;
        reservation.plate = data.plate ? data.plate : null;
        if (data.mechanical_orders && data.mechanical_orders.length > 0) {
            reservation.mechanical_orders = _.map(data.mechanical_orders, (mechanicalOrder) => {
                if (+mechanicalOrder.status > 5)
                    reservation.keysNeeded = true;
                return MechanicalOrderBuilder.buildMechanicalOrder(mechanicalOrder);
            });
        }
        if (+data.pvp_mechanical_orders > 0)
            reservation.keysNeeded = true;
        if (data.untouched_reservation) {
            reservation.untouched_reservation = this.buildReservation(data.untouched_reservation);
        }
        reservation.travelling_for_work = data.travelling_for_work;
        reservation.has_customized_service_request = data.has_customized_service_request;
        if (data.customized_service_request) {
            reservation.customized_service_request = CustomizedServiceRequestBuilder.buildCustomizedServiceRequest(data.customized_service_request);
        }
        reservation.customized_tires_request = data.customized_tires_request;
        reservation.retrieve_timestamp = new Date();
        reservation.key_left_by_client_timestamp = data.key_left_by_client_timestamp;
        reservation.key_picked_by_client_timestamp = data.key_picked_by_client_timestamp;
        reservation.shuttle_picked_checkin_timestamp = data.shuttle_picked_checkin_timestamp;
        reservation.shuttle_picked_checkout_timestamp = data.shuttle_picked_checkout_timestamp;
        reservation.client_at_departure_terminal_timestamp = data.client_at_departure_terminal_timestamp;
        reservation.client_at_arrival_terminal_timestamp = data.client_at_arrival_terminal_timestamp;
        reservation.premium_checkin_start_timestamp = data.premium_checkin_start_timestamp;
        reservation.premium_checkout_start_timestamp = data.premium_checkout_start_timestamp;
        reservation.premium_checkin_request_timestamp = data.premium_checkin_request_timestamp;
        reservation.premium_checkout_request_timestamp = data.premium_checkout_request_timestamp;
        reservation.premium_checkin_agent_arrive_timestamp = data.premium_checkin_agent_arrive_timestamp;
        reservation.premium_checkout_agent_arrive_timestamp = data.premium_checkout_agent_arrive_timestamp;
        reservation.premium_checkout_car_picked_timestamp = data.premium_checkout_car_picked_timestamp;
        reservation.premium_checkout_agent_pick_parking_ticket_timestamp = data.premium_checkout_agent_pick_parking_ticket_timestamp;
        reservation.premium_checkout_agent_renew_parking_ticket_timestamp = data.premium_checkout_agent_renew_parking_ticket_timestamp;
        reservation.premium_checkin_client_arrive_timestamp = data.premium_checkin_client_arrive_timestamp;
        reservation.premium_checkout_client_arrive_timestamp = data.premium_checkout_client_arrive_timestamp;
        reservation.lot_premium_panic = data.lot_premium_panic;
        reservation.premium_checkin_request_via_id = data.premium_checkin_request_via_id;
        return reservation;
    }
    static buildAdminKeySpaceInformation(obj) {
        let key_space_information = {
            id_key_space: obj.id_key_space,
            block: obj.block,
            col: obj.col,
            row: obj.row,
            id_key_zone: obj.id_key_zone,
            key_zone_name: obj.key_zone_name,
            preceding_vehicles: obj.preceding_vehicles,
            is_buffer: obj.is_buffer,
            preceding_spaces: obj.preceding_spaces ? obj.preceding_spaces.map(obj => {
                return {
                    id_reservation: obj.id_reservation,
                    plate: obj.plate,
                    space_number: obj.space_number,
                    calle: obj.calle,
                };
            }) : null
        };
        return key_space_information;
    }
    static buildLocationObject(obj) {
        let locationObj = Object.assign(obj);
        return locationObj;
    }
    static buildReservationInstructions(check) {
        let data = {};
        for (let info in check) {
            if (check[info] instanceof Array) {
                data[info] = _.map(check[info], (_check) => {
                    return ProductBuilder.buildInfoSection(_check);
                });
            }
            else {
                data[info] = this.buildLocationObject(check[info]);
            }
        }
        return data;
    }
    static buildReservationProduct(data) {
        const product = new ReservationProduct();
        product.productId = data.product_id;
        product.productName = data.product_name;
        product.productType = data.product_type;
        product.provider = data.provider;
        product.service = data.service;
        product.price = data.product_price;
        product.paid = data.paid;
        return product;
    }
    static buildReservationExtra(data) {
        const extra = new ReservationExtra();
        extra.extra = ProductBuilder.buildExtra(data);
        extra.paid = data.paid;
        extra.extra_id = data.extra_id;
        extra.name = data.name;
        extra.price = data.price;
        extra.type = data.type;
        extra.keys_needed = data.keys_needed;
        extra.service_type = data.service_type;
        extra.description = data.description;
        extra.long_description = data.long_description;
        extra.price_calculation = data.price_calculation;
        extra.unitary_price = data.unitary_price;
        extra.amount = data.amount;
        extra.pvp_unit = data.pvp_unit;
        extra.pvp = data.pvp;
        extra.vat = data.vat;
        extra.base = data.base;
        extra.separated_bill = data.separated_bill;
        extra.id_invoice_profile = data.id_invoice_profile;
        extra.date_requested = data.date_requested;
        extra.date_started = data.date_started;
        extra.date_completed = data.date_completed;
        extra.date_dimissed = data.date_dimissed;
        extra.status = data.status;
        extra.reason_code = data.reason_code;
        extra.icon = data.icon;
        extra.image_gallery = data.image_gallery ? ImageGalleryBuilder.buildImageGallery(data.image_gallery) : null;
        return extra;
    }
    static buildTrip(data) {
        const trip = new ReservationTrip();
        trip.driver = data.driver;
        trip.phone = data.phone;
        trip.email = data.email;
        trip.zip = data.zip;
        trip.record = data.record;
        trip.checkInTerminalId = data.in_terminal;
        trip.checkInNumPassengers = data.in_passengers;
        trip.checkOutTerminalId = data.out_terminal;
        trip.checkOutNumPassengers = data.out_passengers;
        trip.vehicle = VehicleBuilder.buildVehicle(data.vehicle);
        trip.baggage = data.baggage;
        trip.flightNumber = data.flight_number;
        trip.fuelLevelIn = data.premium_in_car_fuel_level;
        trip.kmIn = data.premium_in_car_km;
        trip.fuelLevelOut = data.premium_out_car_fuel_level;
        trip.kmOut = data.premium_out_car_km;
        trip.ship = data.ship;
        return trip;
    }
    static buildLogEvent(data) {
        const logEvent = new ReservationLogEvent();
        logEvent.timestamp = newDateBuilder(data.timestamp);
        logEvent.source = data.source;
        logEvent.event = data.event;
        return logEvent;
    }
    static buildPremiumStatus(data) {
        const status = new StatusPremium();
        if (data['check-in']) {
            status.checkInStatus = this.buildPremiumCheckStatus(data['check-in']);
            status.checkOutStatus = this.buildPremiumCheckStatus(data['check-out']);
        }
        else if (data['premium']['check-in']) {
            status.checkInStatus = this.buildPremiumCheckStatus(data['premium']['check-in']);
            status.checkOutStatus = this.buildPremiumCheckStatus(data['premium']['check-out']);
        }
        if (Object.keys(data.client) && Object.keys(data.client).length) {
            status.clientStatus = this.buildClientStatus(data.client);
        }
        if (Object.keys(data.agent) && Object.keys(data.agent).length) {
            status.agentStatus = this.buildAgentStatus(data.agent);
        }
        return status;
    }
    static buildPremiumCheckStatus(status) {
        const currentStatus = status && status.toLowerCase();
        switch (currentStatus) {
            case 'unconfirmed':
                return PremiumStatusEnum.unconfirmed;
            case 'requested':
                return PremiumStatusEnum.requested;
            case 'completed':
                return PremiumStatusEnum.completed;
            default:
                return null;
        }
    }
    static buildClientStatus(data) {
        const status = new ClientStatus();
        status.timestamp = newDateBuilder(data.timestamp);
        status.waiting = data.waiting;
        status.position = LocationBuilder.buildLocation(data.position);
        status.prediction = LocationBuilder.buildPrediction(data.prediction);
        return status;
    }
    static buildAgentStatus(data) {
        const status = new AgentStatus();
        status.agentId = data.agent_id;
        status.status = data['agent_status'];
        status.agentName = data.name;
        status.pictureUrl = data.photo;
        status.phone = data.phone;
        status.waiting = data.waiting;
        status.isAssigned = data.is_agent_assigned;
        status.isQueued = data.is_queued;
        status.isRequested = status.isQueued || status.status === AGENT_STATUS.SERVICE_REQUESTED;
        status.multireception = data.multireception;
        status.id_multireception_terminal = data.id_multireception_terminal;
        status.agentLastStatus = this.buildAgentLastStatus(data.agent_status);
        status.registeringClientInformation = data.registering_client_information;
        if (data.position) {
            status.position = LocationBuilder.buildLocation(data.position);
        }
        if (data.prediction) {
            status.prediction = LocationBuilder.buildPrediction(data.prediction);
        }
        return status;
    }
    static buildAgentLastStatus(data) {
        const normalizedData = data && data.toLowerCase();
        switch (normalizedData) {
            case 'check-in':
                return AgentLastStatusEnum.checkIn;
            case 'check-in_registering_client_information':
                return AgentLastStatusEnum.checkInRegisteringInformation;
            case 'check-in_transaction_completed':
                return AgentLastStatusEnum.checkInTransactionCompleted;
            case 'check-out':
                return AgentLastStatusEnum.checkOut;
            case 'check-out_registering_client_information':
                return AgentLastStatusEnum.checkOutRegisteringInformation;
            default:
                return null;
        }
    }
    static buildReservationPayload(reservation) {
        const extras = _.map(reservation.extras, (id) => {
            return { extra_id: id };
        });
        return {
            client_id: reservation.client_id || null,
            id_agency: reservation.id_agency || null,
            profile_id: reservation.profileId,
            company_id: reservation.companyId,
            use_points: reservation.usePoints,
            is_alternative_reservation: reservation.is_alternative_reservation,
            paymethod_id: reservation.paymentMethodId,
            product_id: reservation.productId,
            start_date: reservation.startDate.toISOString(),
            end_date: reservation.endDate.toISOString(),
            promocode: reservation.promoCode,
            accept_terms_timestamp: reservation.accept_terms_timestamp,
            accept_promos: reservation.accept_promos,
            accept_newsletter: reservation.accept_newsletter,
            trip: this.buildReservationTripPayload(reservation.trip),
            extras,
            agentObservations: reservation.agentObservations || null,
            spaceNumber: reservation.spaceNumber || null,
            calle: reservation.calle || null,
            idImageInSignature: reservation.idImageInSignature || null,
            invoice_requested: reservation.invoice_requested || null,
            invoice_address: reservation.invoice_address || null,
            invoice_city: reservation.invoice_city || null,
            invoice_email: reservation.invoice_email || null,
            invoice_name: reservation.invoice_name || null,
            invoice_nif: reservation.invoice_nif || null,
            invoice_state: reservation.invoice_state || null,
            invoice_zip: reservation.invoice_zip || null,
            invoice_country: reservation.invoice_country || null,
            hash: reservation.hash || null,
            via: reservation.via,
            id_mechanical_estimate: reservation.id_mechanical_estimate || null,
            travelling_for_work: reservation.travelling_for_work || null,
            notes: reservation.notes || null,
            customized_service_request: reservation.customized_service_request || null,
            customized_tires_request: reservation.customized_tires_request,
            absuid: reservation.absuid || null,
            form_version: reservation.form_version || null,
            step: reservation.step || null,
            experiment: reservation.experiment || null,
            screen_resolution: reservation.screen_resolution || null,
            key_left_by_client_timestamp: reservation.key_left_by_client_timestamp || null,
            client_at_departure_terminal_timestamp: reservation.client_at_departure_terminal_timestamp || null,
            client_at_arrival_terminal_timestamp: reservation.client_at_arrival_terminal_timestamp || null,
            key_picked_by_client_timestamp: reservation.key_picked_by_client_timestamp || null,
            shuttle_picked_checkin_timestamp: reservation.shuttle_picked_checkin_timestamp || null,
            shuttle_picked_checkout_timestamp: reservation.shuttle_picked_checkout_timestamp || null,
            premium_checkin_start_timestamp: reservation.premium_checkin_start_timestamp || null,
            premium_checkout_start_timestamp: reservation.premium_checkout_start_timestamp || null,
            premium_checkin_request_timestamp: reservation.premium_checkin_request_timestamp || null,
            premium_checkout_request_timestamp: reservation.premium_checkout_request_timestamp || null,
            premium_checkin_agent_arrive_timestamp: reservation.premium_checkin_agent_arrive_timestamp || null,
            premium_checkout_agent_arrive_timestamp: reservation.premium_checkout_agent_arrive_timestamp || null,
            premium_checkout_car_picked_timestamp: reservation.premium_checkout_car_picked_timestamp || null,
            premium_checkout_agent_pick_parking_ticket_timestamp: reservation.premium_checkout_agent_pick_parking_ticket_timestamp || null,
            premium_checkout_agent_renew_parking_ticket_timestamp: reservation.premium_checkout_agent_renew_parking_ticket_timestamp || null,
            premium_checkin_client_arrive_timestamp: reservation.premium_checkin_client_arrive_timestamp || null,
            premium_checkout_client_arrive_timestamp: reservation.premium_checkout_client_arrive_timestamp || null,
            premium_checkin_request_via_id: reservation.premium_checkin_request_via_id || null,
            utm_source: reservation.utm_source || null,
            utm_medium: reservation.utm_medium || null,
            utm_campaign: reservation.utm_campaign || null,
            utm_term: reservation.utm_term || null,
        };
    }
    static buildReservationPaymentUpdatePayload(paymethod_id, profile_id, use_points) {
        return {
            profile_id,
            paymethod_id,
            use_points
        };
    }
    static buildReservationVehicleUpdatePayload(vehicle) {
        return {
            trip: { vehicle: (_.isEmpty(vehicle)) ? null : vehicle }
        };
    }
    static buildReservationDateUpdatePayload(start_date, end_date) {
        return {
            start_date,
            end_date
        };
    }
    static buildReservationTerminalCheckInPayload(in_terminal) {
        return {
            trip: {
                in_terminal
            }
        };
    }
    static buildReservationTerminalCheckOutPayload(out_terminal) {
        return {
            trip: {
                out_terminal
            }
        };
    }
    static buildReservationPassengersCheckInPayload(in_passengers) {
        return {
            trip: {
                in_passengers
            }
        };
    }
    static buildReservationPassengersCheckOutPayload(out_passengers) {
        return {
            trip: {
                out_passengers
            }
        };
    }
    static buildReservationFlightNumberPayload(flight_number) {
        return {
            trip: {
                flight_number
            }
        };
    }
    static buildReservationTripPayload(trip) {
        const vehicle = trip.vehicle ? VehicleBuilder.buildVehiclePayload(trip.vehicle) : null;
        return {
            baggage: trip.baggage,
            driver: trip.driver,
            phone: trip.phone,
            email: trip.email,
            zip: trip.zip,
            record: trip.record,
            in_terminal: trip.checkInTerminalId,
            in_passengers: trip.checkInNumPassengers,
            out_terminal: trip.checkOutTerminalId,
            out_passengers: trip.checkOutNumPassengers,
            vehicle: (_.isEmpty(vehicle)) ? null : vehicle,
            flight_number: trip.flightNumber || null,
            premium_in_car_fuel_level: trip.fuelLevelIn,
            premium_in_car_km: trip.kmIn,
            premium_out_car_fuel_level: trip.fuelLevelOut,
            premium_out_car_km: trip.kmOut,
            ship: trip.ship
        };
    }
    static buildPremiumServiceRequestPayload(service) {
        if (service.prediction && service.position) {
            return {
                position: LocationBuilder.buildLocation(service.position),
                prediction: LocationBuilder.buildPrediction(service.prediction)
            };
        }
        if (service.position) {
            return {
                position: LocationBuilder.buildLocation(service.position),
            };
        }
        if (service.prediction) {
            return {
                prediction: LocationBuilder.buildPrediction(service.prediction)
            };
        }
    }
    static buildReservationRequest(reservation) {
        const reservationRequest = new ReservationRequest();
        reservationRequest.id = reservation.id;
        reservationRequest.client_id = reservation.userId;
        reservationRequest.profileId = reservation.profileId;
        reservationRequest.usePoints = reservation.usePoints;
        reservationRequest.paymentMethodId = reservation.paymentMethodId;
        reservationRequest.productId = reservation.product.productId;
        reservationRequest.startDate = reservation.startDate;
        reservationRequest.endDate = reservation.endDate;
        reservationRequest.promoCode = reservation.promos && reservation.promos.length ? reservation.promos[0].name : undefined;
        reservationRequest.trip = reservation.trip;
        reservationRequest.extras = reservation.extras.map((extra) => {
            return extra.extra.id;
        });
        reservationRequest.companyId = reservation.companyId;
        reservationRequest.id_agency = reservation.id_agency;
        reservationRequest.via = reservation.via;
        reservationRequest.is_alternative_reservation = reservation.is_alternative_reservation ? reservation.is_alternative_reservation : 0;
        return reservationRequest;
    }
    static buildReservationBillingDetailsPayload(reservation) {
        return {
            invoice_requested: reservation.invoice_requested,
            invoice_name: reservation.invoice_name,
            invoice_nif: reservation.invoice_nif,
            invoice_email: reservation.invoice_email,
            invoice_address: reservation.invoice_address,
            invoice_city: reservation.invoice_city,
            invoice_zip: reservation.invoice_zip,
            invoice_state: reservation.invoice_state,
            invoice_country: reservation.invoice_country
        };
    }
    static buildOffer(data) {
        const dataOffer = data;
        const offer = new Offer();
        offer.productId = dataOffer.product_id;
        offer.startDate = newDateBuilder(dataOffer.start_date);
        offer.endDate = newDateBuilder(dataOffer.end_date);
        offer.productPrice = dataOffer.product_price;
        offer.productPrice_aena = dataOffer.product_price_aena;
        offer.pvp_product_total = dataOffer.pvp_product_total;
        offer.pvp_additional = dataOffer.pvp_additional;
        offer.pvp_carservice_discount = dataOffer.pvp_carservice_discount;
        offer.pvp_product_dto = dataOffer.pvp_product_dto;
        offer.pvp_product_option = dataOffer.pvp_product_option;
        offer.pvp_product_points = dataOffer.pvp_product_points;
        offer.pvp_product_promotion = dataOffer.pvp_product_promotion;
        offer.pvp_product_raw = dataOffer.pvp_product_raw;
        offer.pvp_product_std = dataOffer.pvp_product_std;
        offer.pvp_product_trans = dataOffer.pvp_product_trans;
        offer.pvp_wash = dataOffer.pvp_wash;
        offer.pvp_double_space = dataOffer.pvp_double_space;
        offer.pvp_mechanical_orders = dataOffer.pvp_mechanical_orders;
        offer.pvp_mechanical_order_ferry = dataOffer.pvp_mechanical_order_ferry;
        offer.pvp_extra_services = dataOffer.pvp_extra_services;
        offer.vat_additional = dataOffer.vat_additional;
        offer.vat_standard = dataOffer.vat_standard;
        offer.vat_trans = dataOffer.vat_trans;
        offer.base_additional = dataOffer.base_additional;
        offer.base_standard = dataOffer.base_standard;
        offer.base_trans = dataOffer.base_trans;
        offer.promos = [];
        offer.mechanical_orders = dataOffer.mechanical_orders ? _.map(dataOffer.mechanical_orders, (mechanicalOrder) => {
            return MechanicalOrderBuilder.buildMechanicalOrder(mechanicalOrder);
        }) : null;
        if (dataOffer.promos) {
            offer.promos =
                dataOffer.promos.map((promo) => ProductBuilder.buildPromo(promo));
        }
        offer.price = dataOffer.total_price;
        offer.hash = dataOffer.hash;
        offer.extras = this.buildExtras(dataOffer.extras);
        return offer;
    }
}
ReservationBuilder.ɵfac = function ReservationBuilder_Factory(t) { return new (t || ReservationBuilder)(); };
ReservationBuilder.ɵprov = i0.ɵɵdefineInjectable({ token: ReservationBuilder, factory: ReservationBuilder.ɵfac });
(function () { i0.ɵsetClassMetadata(ReservationBuilder, [{
        type: Injectable
    }], null, null); })();
//# sourceMappingURL=reservation-builder.js.map